import React, { useEffect, useRef, useState } from "react";
import { Row, Col, Form, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Formik, FieldArray } from "formik";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import { jwtDecode } from "jwt-decode";
import Card from "../../../components/Card";
import url from "../../../configs/config";
import FocusError from "../../../components/error/error-focus";
import AlertNotificationSimple from "../../../components/alert/alert-notification-simple";
import InputField from "../../../components/input/input-field";
import { roleTypes } from "../../../data/roleType";

const SystemAdd = () => {
  const alertRef = useRef(null);
  const navigate = useNavigate();

  const [errors, setErrors] = useState({});
  const [decodedToken, setDecodedToken] = useState();
  const token = localStorage.getItem("coyote-token");
  const [alert, setAlert] = useState({
    message: "",
    type: "",
    show: false,
    alertFor: "",
  });
  const initialFormData = { 
    name: "", 
    code: "",
    phases: [
      {
        name: "",
        code: "",
      },
    ],
  };

  useEffect(() => {
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setDecodedToken(decoded?.user);
      } catch (error) {
        console.error("Failed to decode token", error);
      }
    }
  }, [token]);

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);
    try {
      const config = {
        headers: { Authorization: `${token}` },
      };
      const response = await axios.post(
        `${url.backendURL}system/`,
        values,
        config
      );

      if (alertRef.current) {
        alertRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }

      setAlert({
        message: response.data.message,
        type: "success",
        show: true,
        alertFor: "system-add",
      });

      setErrors({});
      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
        navigate(`/dashboard/app/system-list`);
      }, 2000);
    } catch (err) {
      handleError(err, "system-add");
    } finally {
      setSubmitting(false);
    }
  };

  const handleError = (err, alertFor) => {
    const { response } = err;
    if (alertRef.current) {
      alertRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    if (
      response.data.error === "jwt expired" ||
      response?.data?.error === "Unauthorized. User not found"
    ) {
      localStorage.removeItem("coyote-token");

      navigate(
        `/${
          decodedToken?.roles?.some(
            (role) => role.role_type === roleTypes.SUPER_ADMIN
          )
            ? "admin"
            : "user"
        }/auth/sign-in`,
        {
          state: { status: "expired" },
        }
      );
    } else {
      const responseErrors = response?.data?.error || {};
      setErrors(responseErrors);
      setAlert({
        message: response ? response.data.message : "An error occurred",
        type: "danger",
        show: true,
        alertFor,
      });
      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
      }, 5000);
    }
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .matches(
        /^(?=.*[a-zA-Z0-9])[a-zA-Z0-9!@#$%^&*()_+=[\]{};':"\\|,.<>/?-\s]*$/,
        "System name must contain at least one letter or number"
      )
      .min(1, "System name must be at least 1 character long")
      .max(255, "System name must not exceed 255 characters")
      .trim()
      .required("System Name is required"),
    code: Yup.string()
      .min(1, "Code must be at least 1 character long")
      .max(20, "Code must not exceed 20 characters")
      .trim()
      .required("Code is required"),
    phases: Yup.array()
      .of(
        Yup.object().shape({
          name: Yup.string().when(["code"], {
            is: (code) => code && code.trim(),
            then: (schema) =>
              schema
                .matches(
                  /^(?=.*[a-zA-Z0-9])[a-zA-Z0-9!@#$%^&*()_+=[\]{};':"\\|,.<>/?-\s]*$/,
                  "Phase name must contain at least one letter or number"
                )
                .min(1, "Phase name must be at least 1 character long")
                .max(255, "Phase name must not exceed 255 characters")
                .trim()
                .required("Phase name is required"),
            otherwise: (schema) =>
              schema
                .matches(
                  /^(?=.*[a-zA-Z0-9])[a-zA-Z0-9!@#$%^&*()_+=[\]{};':"\\|,.<>/?-\s]*$/,
                  "Phase name must contain at least one letter or number"
                )
                .min(1, "Phase name must be at least 1 character long")
                .max(255, "Phase name must not exceed 255 characters")
                .trim()
                .nullable(),
          }),
          code: Yup.string().when(["name"], {
            is: (name) => name && name.trim(),
            then: (schema) =>
              schema
                .min(1, "Code must be at least 1 character long")
                .max(20, "Code must not exceed 20 characters")
                .trim()
                .required("Code is required"),
            otherwise: (schema) =>
              schema
                .min(1, "Code must be at least 1 character long")
                .max(20, "Code must not exceed 20 characters")
                .trim()
                .nullable(),
          }),
        }, [["name", "code"]])
      )
      .max(10, "Maximum 10 phases allowed")
      .nullable(),
  });

  const handleReset = (resetForm) => {
    resetForm();
    setErrors({});
  };

  return (
    <>
      <Formik
        initialValues={initialFormData}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
        validate={(values) => {
          const errors = {};
          if (values.phases && values.phases.length > 1) {
            const phaseNames = [];
            const phaseCodes = [];
            const phaseErrors = [];

            values.phases.forEach((phase, index) => {
              const phaseError = {};
              if (phase.name) {
                const normalizedName = phase.name.trim().toLowerCase();
                if (normalizedName && phaseNames.includes(normalizedName)) {
                  phaseError.name = "Phase with same name already exists";
                } else if (normalizedName) {
                  phaseNames.push(normalizedName);
                }
              }

              if (phase.code) {
                const normalizedCode = phase.code.trim().toLowerCase();
                if (normalizedCode && phaseCodes.includes(normalizedCode)) {
                  phaseError.code = "Phase with same code already exists";
                } else if (normalizedCode) {
                  phaseCodes.push(normalizedCode);
                }
              }

              if (Object.keys(phaseError).length > 0) {
                phaseErrors[index] = phaseError;
              }
            });

            if (phaseErrors.length > 0) {
              errors.phases = phaseErrors;
            }
          }
          return errors;
        }}
      >
        {({
          values,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          resetForm,
          errors: formikErrors,
          touched,
        }) => (
          <Form onSubmit={handleSubmit}>
            <FocusError />
            <Row>
              <Col md="12" ref={alertRef}>
                <Card>
                  <Card.Header className="d-flex justify-content-between">
                    <div
                      className="breadcrumb header-title"
                      style={{ cursor: "pointer" }}
                    >
                      <h4
                        onClick={() => navigate("/dashboard/app/system-list")}
                        className="text-primary card-title"
                        style={{ cursor: "pointer" }}
                      >
                        <span>System</span>
                      </h4>
                      <h4 className="mx-2 card-title">{">"}</h4>
                      <h4 className="card-title">Add</h4>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <AlertNotificationSimple
                      type={alert.type}
                      message={alert.message}
                      show={alert.show}
                      onClose={() => setAlert({ ...alert, show: false })}
                    />
                    <div className="new-system-info">
                      <Row>
                        <InputField
                          className="col-md-6 form-group"
                          type="text"
                          name="name"
                          placeholder="Enter name"
                          value={values.name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          asterick={true}
                          errorMessage={errors?.name}
                          label="Name"
                        />
                        <InputField
                          className="col-md-6 form-group"
                          type="text"
                          name="code"
                          placeholder="Enter code"
                          value={values.code}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          asterick={true}
                          errorMessage={errors?.code}
                          label="Code"
                        />
                      </Row>

                      <hr className="my-4" />

                      <div className="phases-info">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h5 className="text-primary mb-0">Phase Information (Optional)</h5>
                        </div>

                        <FieldArray name="phases">
                          {({ push, remove }) => (
                            <>
                              {values.phases.map((phase, index) => (
                                <div
                                  key={index}
                                  className="phase-item mb-4 p-3"
                                  style={{ border: "1px solid #e9ecef", borderRadius: "8px" }}
                                >
                                  <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="mb-0">Phase {index + 1}</h6>
                                    {values.phases.length > 1 && (
                                      <Button
                                        type="button"
                                        variant="btn btn-outline-danger btn-sm"
                                        onClick={() => remove(index)}
                                      >
                                        Remove Phase
                                      </Button>
                                    )}
                                  </div>

                                  <Row>
                                    <InputField
                                      className="col-md-6 form-group"
                                      type="text"
                                      name={`phases[${index}].name`}
                                      placeholder="Enter phase name"
                                      value={phase.name}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      asterick={false}
                                      errorMessage={
                                        errors?.phases?.[index]?.name
                                      }
                                      label="Phase Name"
                                    />
                                    <InputField
                                      className="col-md-6 form-group"
                                      type="text"
                                      name={`phases[${index}].code`}
                                      placeholder="Enter code"
                                      value={phase.code}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      asterick={false}
                                      errorMessage={
                                        errors?.phases?.[index]?.code
                                      }
                                      label="Code"
                                    />
                                  </Row>
                                </div>
                              ))}

                              <div className="mb-4">
                                <Button
                                  type="button"
                                  variant="btn btn-primary"
                                  onClick={() => push({ name: "", code: "" })}
                                  disabled={values.phases.length >= 10}
                                >
                                  + Add New Phase
                                </Button>
                              </div>
                            </>
                          )}
                        </FieldArray>
                      </div>

                      <hr />
                      <div className="d-flex flex-wrap flex-md-nowrap justify-content-end gap-1 gap-md-2">
                        <Button
                          type="button"
                          className="me-2 me-md-5 px-3 px-md-4"
                          variant="btn btn-light"
                          onClick={() => handleReset(resetForm)} // Reset the form
                        >
                          Reset
                        </Button>

                        <Button
                          type="submit"
                          disabled={isSubmitting}
                          variant="btn btn-primary"
                          className="me-2 me-md-5 px-3 px-md-4"
                        >
                          Submit
                        </Button>
                        <Button
                          type="button"
                          className=" px-3 px-md-4"
                          variant="btn btn-danger"
                          onClick={() => navigate("/dashboard/app/system-list")}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default SystemAdd;
